import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  InputNumber, 
  Input, 
  Space, 
  Popconfirm, 
  message, 
  Select,
  Typography,
  Tag,
  DatePicker 
} from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { tollFeeApi, vehicleCapacityTonsApi, shippingPriceApi } from '../services/api';
import { TollFee, VehicleCapacityTons, Province, District } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

const { Title } = Typography;
const { Option } = Select;

const TollFeePage: React.FC = () => {
  const { t } = useLanguage();
  const [tollFees, setTollFees] = useState<TollFee[]>([]);
  const [capacityTons, setCapacityTons] = useState<VehicleCapacityTons[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<TollFee | null>(null);
  const [selectedHistory, setSelectedHistory] = useState<any[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tollFeesRes, capacityRes, provincesRes] = await Promise.all([
        tollFeeApi.getAll(),
        vehicleCapacityTonsApi.getAll(),
        shippingPriceApi.getProvinces()
      ]);
      
      setTollFees(tollFeesRes.data);
      setCapacityTons(capacityRes.data);
      setProvinces(provincesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to fetch toll fee data');
    } finally {
      setLoading(false);
    }
  };

  const handleProvinceChange = async (provinceId: number | undefined) => {
    if (provinceId) {
      try {
        const response = await shippingPriceApi.getDistricts(provinceId);
        setDistricts(response.data);
        form.setFieldsValue({ districtId: undefined }); // Reset district selection
      } catch (error) {
        console.error('Error fetching districts:', error);
        message.error('Failed to fetch districts');
      }
    } else {
      setDistricts([]);
      form.setFieldsValue({ districtId: undefined });
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setDistricts([]);
    form.setFieldsValue({
      affectedDate: dayjs()
    });
    setModalVisible(true);
  };

  const handleEdit = (item: TollFee) => {
    setEditingItem(item);
    form.setFieldsValue({
      latitude: item.latitude,
   longitude: item.longitude,
      address: item.address,
      fee: item.fee,
      notes: item.notes,
      affectedDate: dayjs(item.affectedDate)
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await tollFeeApi.delete(id);
      message.success('Toll fee deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting toll fee:', error);
      message.error('Failed to delete toll fee');
    }
  };

  const handleViewHistory = async (item: TollFee) => {
    try {
      const response = await tollFeeApi.getHistory(item.id);
      setSelectedHistory(response.data);
      setHistoryModalVisible(true);
    } catch (error) {
      console.error('Error fetching history:', error);
      message.error('Failed to fetch toll fee history');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const formattedValues = {
        ...values,
        affectedDate: values.affectedDate ? values.affectedDate.format('YYYY-MM-DD') : undefined
 };
 
      if (editingItem) {
        await tollFeeApi.update(editingItem.id, formattedValues);
        message.success('Toll fee updated successfully');
      } else {
        await tollFeeApi.create(formattedValues);
        message.success('Toll fee created successfully');
      }
    
      setModalVisible(false);
      form.resetFields();
      setDistricts([]);
      fetchData();
    } catch (error: any) {
      console.error(`Error ${editingItem ? 'updating' : 'creating'} toll fee:`, error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      message.error(`Error ${editingItem ? 'updating' : 'creating'} toll fee: ${errorMessage}`);
    }
  };

  const handleSeedTollBooths = async () => {
    try {
      message.loading({ content: 'Seeding toll booths...', key: 'seeding', duration: 0 });
      await tollFeeApi.seedTollBooths();
      message.success({ content: 'Toll booths seeded successfully! Check the table below.', key: 'seeding', duration: 5 });
      fetchData();
    } catch (error: any) {
      console.error('Error seeding toll booths:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to seed toll booths';
      message.error({ content: `Error: ${errorMessage}`, key: 'seeding', duration: 5 });
    }
  };

  const columns: ColumnsType<TollFee> = [
    {
      title: t('capacity.tons'),
      dataIndex: 'capacityTons',
      key: 'capacityTons',
      sorter: (a, b) => a.capacityTons - b.capacityTons,
      render: (value) => `${value} ${t('tons')}`
    },
    {
      title: t('location'),
      key: 'location',
      render: (_, record) => (
        <div>
          <div>{record.locationName}</div>
          <Tag color={record.locationType === 'Province' ? 'blue' : 'green'}>
            {t(record.locationType === 'Province' ? 'province' : 'district')}
          </Tag>
          {record.latitude && record.longitude && (
       <div className="text-muted small mt-1">
         <i className="bi bi-geo-alt"></i> {record.latitude.toFixed(4)}, {record.longitude.toFixed(4)}
       </div>
          )}
          {!record.latitude && !record.longitude && (
       <Tag color="warning" className="mt-1">No GPS coordinates</Tag>
    )}
        </div>
      )
    },
    {
      title: t('fee.amount'),
      dataIndex: 'fee',
      key: 'fee',
      sorter: (a, b) => a.fee - b.fee,
      render: (value) => new Intl.NumberFormat('vi-VN', { 
        style: 'currency', 
        currency: 'VND' 
      }).format(value)
    },
    {
      title: t('affected.date'),
      dataIndex: 'affectedDate',
      key: 'affectedDate',
      sorter: (a: TollFee, b: TollFee) => new Date(a.affectedDate).getTime() - new Date(b.affectedDate).getTime(),
      render: (value: string) => new Date(value).toLocaleDateString()
    },
    {
      title: t('notes'),
      dataIndex: 'notes',
      key: 'notes',
      render: (value) => value || '-'
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('active') : t('inactive')}
        </Tag>
      ),
      filters: [
        { text: t('active'), value: true },
        { text: t('inactive'), value: false }
      ],
      onFilter: (value, record) => record.isActive === value
    },
    {
      title: t('created.by'),
      dataIndex: 'createdByUserName',
      key: 'createdByUserName'
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            title={t('edit')}
          />
          <Button
            icon={<HistoryOutlined />}
            onClick={() => handleViewHistory(record)}
            size="small"
            title={t('shipping.prices.history')}
          />
          <Popconfirm
            title={t('delete.confirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('yes')}
            cancelText={t('no')}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              title={t('delete')}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const historyColumns = [
    {
      title: t('action'),
   dataIndex: 'action',
      key: 'action'
    },
    {
    title: t('old.price'),
      dataIndex: 'oldFee',
      key: 'oldFee',
      render: (value: number) => value ? new Intl.NumberFormat('vi-VN', { 
        style: 'currency', 
        currency: 'VND' 
      }).format(value) : '-'
    },
    {
      title: t('new.price'),
      dataIndex: 'newFee',
      key: 'newFee',
      render: (value: number) => new Intl.NumberFormat('vi-VN', { 
        style: 'currency', 
        currency: 'VND' 
      }).format(value)
    },
    {
      title: t('affected.date'),
      dataIndex: 'affectedDate',
      key: 'affectedDate',
      render: (value: string) => value ? new Date(value).toLocaleDateString() : '-'
    },
    {
      title: t('reason'),
  dataIndex: 'reason',
      key: 'reason',
      render: (value: string) => value || '-'
    },
    {
 title: t('changed.by'),
      dataIndex: 'changedByUserName',
      key: 'changedByUserName'
    },
    {
      title: t('time'),
      dataIndex: 'changeDate',
      key: 'changeDate',
      render: (value: string) => new Date(value).toLocaleString()
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
       <Title level={2}>{t('toll.fee.management')}</Title>
  <Space>
      <Popconfirm
         title="Seed Toll Booths"
       description="This will add/update 21 major Vietnam toll booths with GPS coordinates. Existing toll fees will be updated with location data. Continue?"
              onConfirm={handleSeedTollBooths}
       okText="Yes, Seed"
   cancelText="Cancel"
>
       <Button
      type="default"
                icon={<PlusOutlined />}
                style={{ backgroundColor: '#52c41a', color: 'white', borderColor: '#52c41a' }}
         >
      Seed Toll Booths
       </Button>
            </Popconfirm>
         <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleAdd}
            >
   {t('add.toll.fee')}
        </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={tollFees}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />

        <Modal
   title={editingItem ? t('edit.toll.fee') : t('add.toll.fee')}
 open={modalVisible}
 onCancel={() => {
        setModalVisible(false);
    form.resetFields();
   setDistricts([]);
     }}
onOk={() => form.submit()}
    width={700}
   >
          <div className="alert alert-info mb-3">
       <i className="bi bi-info-circle me-2"></i>
       <strong>Tip:</strong> Add GPS coordinates (latitude & longitude) to display this toll booth on route maps. 
    You can find coordinates using Google Maps (right-click ? "What's here?").
          </div>
   
    <Form
            form={form}
    layout="horizontal"
   labelCol={{ span: 8 }}
         wrapperCol={{ span: 16 }}
     onFinish={handleSubmit}
     >
            {!editingItem && (
              <>
                <Form.Item
                  label={t('vehicle.capacity.tons')}
                  name="capacityTonsId"
                  rules={[{ required: true, message: t('please.select') + ' ' + t('vehicle.capacity.tons') }]}
                >
                  <Select placeholder={t('please.select') + ' ' + t('vehicle.capacity.tons')}>
                    {capacityTons.map(ct => (
                      <Option key={ct.id} value={ct.id}>
                        {ct.capacity} {t('tons')} {ct.description && `(${ct.description})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={t('province')}
                  name="provinceId"
                  rules={[{ required: true, message: t('please.select') + ' ' + t('province') }]}
                >
                  <Select
                    placeholder={t('please.select') + ' ' + t('province')}
                    onChange={handleProvinceChange}
                    allowClear
                  >
                    {provinces.map(province => (
                      <Option key={province.id} value={province.id}>
                        {province.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={t('district') + ' (' + t('optional') + ')'}
                  name="districtId"
                >
                  <Select
                    placeholder={t('please.select') + ' ' + t('district') + ' (' + t('optional') + ')'}
                    allowClear
                    disabled={districts.length === 0}
                  >
                    {districts.map(district => (
                      <Option key={district.id} value={district.id}>
                        {district.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            <Form.Item
              label={t('fee.amount')}
              name="fee"
              rules={[
                { required: true, message: t('please.enter') + ' ' + t('fee.amount') },
                { type: 'number', min: 0, message: t('fee.amount') + ' >= 0' }
              ]}
            >
              <InputNumber
                min={0}
                step={1000}
                style={{ width: '100%' }}
                placeholder={t('please.enter') + ' ' + t('fee.amount')}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as unknown as 0}
              />
            </Form.Item>

            <Form.Item
         label="Latitude (Optional)"
name="latitude"
   rules={[
 { type: 'number', min: -90, max: 90, message: 'Latitude must be between -90 and 90' }
       ]}
            >
           <InputNumber
       min={-90}
       max={90}
   step={0.000001}
   style={{ width: '100%' }}
       placeholder="e.g., 10.7769 (Optional - for map display)"
         />
          </Form.Item>

       <Form.Item
 label="Longitude (Optional)"
              name="longitude"
            rules={[
       { type: 'number', min: -180, max: 180, message: 'Longitude must be between -180 and 180' }
       ]}
   >
       <InputNumber
                min={-180}
    max={180}
 step={0.000001}
  style={{ width: '100%' }}
    placeholder="e.g., 106.7009 (Optional - for map display)"
   />
   </Form.Item>

    <Form.Item
       label="Address (Optional)"
       name="address"
       >
       <Input
       placeholder="e.g., Toll Booth Binh Thanh (Optional)"
maxLength={500}
            />
            </Form.Item>

      <Form.Item
      label={t('affected.date')}
 name="affectedDate"
  rules={[
         { required: true, message: t('please.select') + ' ' + t('affected.date') }
     ]}
       >
     <DatePicker
    style={{ width: '100%' }}
       placeholder={t('please.select') + ' ' + t('affected.date')}
       />
            </Form.Item>

            <Form.Item
              label={t('notes')}
              name="notes"
            >
              <Input.TextArea
                rows={3}
                placeholder={t('notes') + ' (' + t('optional') + ')'}
              />
            </Form.Item>

            {editingItem && (
              <Form.Item
                label={t('reason')}
                name="reason"
              >
                <Input.TextArea
                  rows={2}
                  placeholder={t('reason.for.updating.toll.fee')}
                />
              </Form.Item>
            )}
          </Form>
        </Modal>

        <Modal
          title={t('toll.fee.change.history')}
          open={historyModalVisible}
          onCancel={() => setHistoryModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setHistoryModalVisible(false)}>
              {t('close')}
            </Button>
          ]}
          width={800}
        >
          <Table
            columns={historyColumns}
            dataSource={selectedHistory}
            rowKey="id"
            pagination={{ pageSize: 5 }}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default TollFeePage;